import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { ParcoursContext } from '../../../../../context/ParcoursContext.js';
import NumberInput from '../../../../molecules/formComponents/numberInput/NumberInput.js';
import Textarea from '../../../../molecules/formComponents/textarea/Textarea.js';
import FecFileInput from '../../../../molecules/formComponents/fecFileInput/FecFileInput.js';
import { formatObjectForPosting, getTotalFromValues, formatNumberWithSpaces } from '../../../../../utils/Utils.js';
import { submitFecFileApi } from '../../../../../api/ParcoursApi.js';
import Tuto from '../../../../atoms/icons/general/tuto/Tuto.js';

function BilanActif({ id, index, secondaryParcours }) {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm();
  const Form = { watch, register, setValue };
  const {
    moveToNextStep,
    getStepToGo,
    showTooltip,
    setGoToStep,
    step,
    setStep,
    submitFormsFdc,
    postValues,
    fillInputsFromEstimationValues,
    estimationValues,
  } = useContext(ParcoursContext);

  // États pour la gestion du fichier FEC
  const [fecFile, setFecFile] = useState(null);
  const [fecFilePreview, setFecFilePreview] = useState(null);
  const [isProcessingFec, setIsProcessingFec] = useState(false);
  const [fecProcessed, setFecProcessed] = useState(false);
  const [fecProcessingError, setFecProcessingError] = useState('');

  useEffect(() => {
    submitFormsFdc.current[index] = handleSubmit(formSubmit);
  }, [step, estimationValues]);

  useEffect(() => {
    fillInputsFromEstimationValues(
      'bilan_actif',
      [
        'capital_souscrit',
        'immobilisations_incorporelles',
        'immobilisations_corporelles',
        'immobilisations_financieres',
        'total_immobilisations',
        'stocks_matiere_premiere',
        'stocks_marchandise',
        'creances_clients',
        'autres_creances',
        'valeurs_mobilieres_placement',
        'disponibilites',
        'charges_constatees_avance',
        'total_actif_circulant',
        'comptes_regularisation',
        'total_actif',
        'note_bilan_actif',
      ],
      setValue,
      estimationValues,
    );
  }, [estimationValues]);

  async function formSubmit(values) {
    const note = values.note_bilan_actif;
    delete values.note_bilan_actif;

    const tmp = { bilan_actif: formatObjectForPosting(values) };
    if (!secondaryParcours) tmp.bilan_actif.note_bilan_actif = note;

    postValues(tmp);

    if (getStepToGo() != 0) {
      setStep(getStepToGo());
      setGoToStep(0);
    } else moveToNextStep(index);
  }

  //calcul auto des inputs
  function calcTotalImmobilisations() {
    const tmp = getValues([
      'capital_souscrit',
      'immobilisations_incorporelles',
      'immobilisations_corporelles',
      'immobilisations_financieres',
    ]);
    setValue('total_immobilisations', getTotalFromValues(tmp));

    calcTotalActif();
  }

  function calcTotalActifCirculant() {
    const tmp = getValues([
      'stocks_matiere_premiere',
      'stocks_marchandise',
      'creances_clients',
      'autres_creances',
      'valeurs_mobilieres_placement',
      'disponibilites',
      'charges_constatees_avance',
    ]);
    setValue('total_actif_circulant', getTotalFromValues(tmp));

    calcTotalActif();
  }

  function calcTotalActif() {
    const tmp = getValues(['total_immobilisations', 'total_actif_circulant', 'comptes_regularisation']);
    setValue('total_actif', getTotalFromValues(tmp));
  }

  function handleFecFileSelection(file, preview) {
    setFecFile(file);
    setFecFilePreview(preview);
    setFecProcessed(false);
    setFecProcessingError('');
  }

  function handleFecFileClear() {
    setFecFile(null);
    setFecFilePreview(null);
    setFecProcessed(false);
    setFecProcessingError('');
  }

  async function handleFecFileProcess(file, preview) {
    if (!file) {
      console.error('Aucun fichier à traiter');
      return;
    }

    if (!estimationValues?.id) {
      console.error("ID d'estimation manquant");
      return;
    }

    setIsProcessingFec(true);
    setFecProcessingError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await submitFecFileApi(estimationValues.id, formData);

      const fecData = response.data;

      handleFecDataResponse(fecData);

      setFecProcessed(true);
    } catch (error) {
      console.error('Erreur lors du traitement du fichier FEC:', error);

      let errorMessage = 'Erreur lors du traitement du fichier FEC.';

      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;

        switch (status) {
          case 400:
            errorMessage = data.message || 'Format de fichier FEC invalide.';
            break;
          case 413:
            errorMessage = 'Le fichier est trop volumineux.';
            break;
          case 422:
            errorMessage = data.message || 'Le fichier FEC ne respecte pas les normes comptables.';
            break;
          case 500:
            errorMessage = 'Erreur serveur lors du traitement du fichier.';
            break;
          default:
            errorMessage = data.message || errorMessage;
        }
      } else if (error.request) {
        errorMessage = 'Erreur de connexion. Veuillez vérifier votre connexion internet.';
      }

      setFecProcessingError(errorMessage);
    } finally {
      setIsProcessingFec(false);
    }
  }

  function handleFecDataResponse(responseData) {
    let parsedData = null;

    if (!responseData) {
      console.error('Aucune donnée reçue du serveur');
      return;
    }

    if (typeof responseData === 'string') {
      const jsonStart = responseData.indexOf('{"');
      if (jsonStart !== -1) {
        const jsonString = responseData.substring(jsonStart);
        try {
          parsedData = JSON.parse(jsonString);
        } catch (e) {
          console.error('Erreur lors du parsing JSON:', e);
          return;
        }
      } else {
        console.error('Aucun JSON trouvé dans la réponse');
        return;
      }
    } else {
      parsedData = responseData;
    }

    let estimation = null;

    if (parsedData && parsedData.estimation) {
      estimation = parsedData.estimation;
    } else {
      const yearKey = parsedData && Object.keys(parsedData).find(k => /^\d{4}$/.test(k));
      const yearObj = yearKey ? parsedData[yearKey] : parsedData;

      if (!yearObj || typeof yearObj !== 'object') {
        console.error('Aucune estimation trouvée dans la réponse');
        return;
      }

      const flat = {};
      Object.keys(yearObj).forEach(k => {
        const v = yearObj[k];
        flat[k] = v && typeof v === 'object' && 'result' in v ? v.result : v;
      });

      estimation = {
        bilan_actif: {},
        bilan_passif: {},
        compte_resultat: {},
        retraitements_remunerations: {},
        retraitements_autres_charges: {},
        rentabilite_retraitee: {},
        dettes_hors_exploitation: {},
        materiel_agencements: {},
        detail_marge: {},
      };

      const bilanActifFields = [
        'capital_souscrit',
        'immobilisations_incorporelles',
        'immobilisations_corporelles',
        'immobilisations_financieres',
        'total_immobilisations',
        'stocks_matiere_premiere',
        'stocks_marchandise',
        'creances_clients',
        'autres_creances',
        'valeurs_mobilieres_placement',
        'disponibilites',
        'charges_constatees_avance',
        'total_actif_circulant',
        'comptes_regularisation',
        'total_actif',
        'note_bilan_actif',
      ];

      const bilanPassifFields = [
        'capitaux_propres',
        'provisions_risques_charges',
        'decouverts',
        'comptes_courants_associes',
        'dettes_fournisseurs',
        'autres_dettes',
        'total_dettes',
        'total_passif',
        'dettes_etablissements_credit',
        'produits_constates_avance',
      ];

      const compteResultatFields = [
        'ventes_marchandise',
        'ventes_marchandise_hebergement',
        'ventes_marchandise_restauration',
        'achats_marchandise',
        'marge_commerciale',
        'total_commissions_nettes',
        'production_prestations_vendues',
        'achats_matiere_premiere',
        'marge_sur_productions',
        'ca_global',
        'marge_brute_globale',
        'achats_charges_externes',
        'valeur_ajoutee',
        'subvention_exploitation',
        'impots_societe',
        'impots_taxes',
        'salaires_annuels',
        'charges_sociales_annuelles',
        'excedent_brut_exploitation',
        'reprises_sur_provisions_transfert_charges',
        'autres_produits',
        'dotations_amortissements_provisions',
        'autres_charges',
        'produits_financiers',
        'charges_financieres',
        'produits_exceptionnels',
        'charges_exceptionnelles',
        'impots_societe',
        'ca_transaction',
        'ca_location',
        'ca_gestion_locative',
        'ca_syndic',
      ];

      const retraitRemFields = [
        'remunerations_dirigeants',
        'cotisations_sociales_dirigeants',
        'remontees_holding_dirigeant',
      ];
      const retraitAutresFields = ['total_retraitements_autres_charges'];
      const rentabiliteFields = ['ebe_retraite', 'total_retraitements'];

      bilanActifFields.forEach(f => {
        if (flat[f] !== undefined) estimation.bilan_actif[f] = flat[f];
      });
      bilanPassifFields.forEach(f => {
        if (flat[f] !== undefined) estimation.bilan_passif[f] = flat[f];
      });
      compteResultatFields.forEach(f => {
        if (flat[f] !== undefined) estimation.compte_resultat[f] = flat[f];
      });
      retraitRemFields.forEach(f => {
        if (flat[f] !== undefined) estimation.retraitements_remunerations[f] = flat[f];
      });
      retraitAutresFields.forEach(f => {
        if (flat[f] !== undefined) estimation.retraitements_autres_charges[f] = flat[f];
      });
      rentabiliteFields.forEach(f => {
        if (flat[f] !== undefined) estimation.rentabilite_retraitee[f] = flat[f];
      });

      if (flat['valeur_materiel_exploitation'] !== undefined)
        estimation.materiel_agencements.valeur_materiel_exploitation = flat['valeur_materiel_exploitation'];
      if (flat['comptes_courants_associes'] !== undefined)
        estimation.bilan_passif.comptes_courants_associes = flat['comptes_courants_associes'];
      if (flat['comptes_courants_associes'] !== undefined)
        estimation.dettes_hors_exploitation.comptes_courants_associes = flat['comptes_courants_associes'];

      estimation.__flat = flat;
    }

    if (!estimation) {
      console.error('Aucune estimation trouvée dans la réponse');
      return;
    }

    if (estimation.bilan_actif) {
      Object.keys(estimation.bilan_actif).forEach(fieldName => {
        if (estimation.bilan_actif[fieldName] !== null && estimation.bilan_actif[fieldName] !== undefined) {
          const raw = estimation.bilan_actif[fieldName];
          const formatted = typeof raw === 'number' || !isNaN(raw) ? formatNumberWithSpaces(String(raw)) : raw;
          try {
            setValue(fieldName, formatted, { shouldDirty: true });
          } catch (e) {
            // continue
          }

          const el = document.getElementById(fieldName);
          if (el) {
            el.value = formatted;
            el.dispatchEvent(new Event('input', { bubbles: true }));
          }
        }
      });

      setTimeout(() => {
        calcTotalImmobilisations();
        calcTotalActifCirculant();
        calcTotalActif();
      }, 100);
    }

    if (estimation.__flat) {
      Object.keys(estimation.__flat).forEach(fieldName => {
        const raw = estimation.__flat[fieldName];
        if (raw === null || raw === undefined) return;
        const formatted = typeof raw === 'number' || !isNaN(raw) ? formatNumberWithSpaces(String(raw)) : raw;

        try {
          setValue(fieldName, formatted, { shouldDirty: true });
        } catch (e) {
          // ignore
        }

        const el = document.getElementById(fieldName);
        if (el) {
          el.value = formatted;
          el.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    }

    const fecDataEvent = new CustomEvent('fecDataProcessed', {
      detail: {
        estimation: estimation,
        source: 'BilanActif',
      },
    });

    window.dispatchEvent(fecDataEvent);
  }

  return (
    <form ref={id} onSubmit={handleSubmit(formSubmit)} style={{ height: step == index ? 'auto' : '0' }}>
      <fieldset className='borderless-fieldset'>
        <h2>Importer votre fichier FEC</h2>
        <FecFileInput
          key={fecProcessed ? 'reset' : 'initial'}
          label='Fichier FEC (.txt, .csv ou .fec)'
          name='fec_file'
          register={register}
          setValue={setValue}
          onChange={handleFecFileSelection}
          onProcess={handleFecFileProcess}
          onClear={handleFecFileClear}
          isProcessing={isProcessingFec}
          isNewEstimation={!estimationValues?.id}
          forceReset={fecProcessed}
          className='mb-md'
        />
        {fecProcessed && (
          <div className='fec-success-message'>
            <p className='text-green text-sm'>
              ✓ Les données du fichier FEC ont été importées avec succès dans les étapes 4, 5 et 6.
            </p>
          </div>
        )}
        {fecProcessingError && (
          <div className='fec-error-message'>
            <p className='text-red text-sm'>✗ {fecProcessingError}</p>
          </div>
        )}
      </fieldset>
      <p className='text-blue mb-3 text-xs'>Veuillez renseigner tous les champs (même si la somme est "0")</p>
      <fieldset className='borderless-fieldset'>
        <h2>Bilan actif</h2>
        <div className='row-1000'>
          <NumberInput
            useForm={Form}
            onChange={calcTotalImmobilisations}
            label={
              <>
                Capital souscrit non appelé <Tuto onClick={() => showTooltip('step_4_tuto_1.webp')} />
              </>
            }
            name='capital_souscrit'
            error={errors?.capital_souscrit && errors.capital_souscrit.message}
            icon='euro'
          />
          <NumberInput
            useForm={Form}
            onChange={calcTotalImmobilisations}
            label={
              <>
                Immobilisations incorporelles <Tuto onClick={() => showTooltip('step_4_tuto_2.webp')} />
              </>
            }
            name='immobilisations_incorporelles'
            error={errors?.immobilisations_incorporelles && errors.immobilisations_incorporelles.message}
            icon='euro'
          />
        </div>
        <div className='row-1000'>
          <NumberInput
            useForm={Form}
            onChange={calcTotalImmobilisations}
            label={
              <>
                Immobilisations corporelles <Tuto onClick={() => showTooltip('step_4_tuto_3.webp')} />
              </>
            }
            name='immobilisations_corporelles'
            error={errors?.immobilisations_corporelles && errors.immobilisations_corporelles.message}
            icon='euro'
          />
          <NumberInput
            useForm={Form}
            onChange={calcTotalImmobilisations}
            label={
              <>
                immobilisations financières <Tuto onClick={() => showTooltip('step_4_tuto_4.webp')} />
              </>
            }
            name='immobilisations_financieres'
            error={errors?.immobilisations_financieres && errors.immobilisations_financieres.message}
            icon='euro'
          />
        </div>
        <NumberInput
          bgColor='var(--main-color)'
          disabled
          useForm={Form}
          label='total immobilisations'
          name='total_immobilisations'
          error={errors?.total_immobilisations && errors.total_immobilisations.message}
          validation={{}}
          icon='euro'
        />
        <div className='row-1000'>
          <NumberInput
            useForm={Form}
            onChange={calcTotalActifCirculant}
            label={
              <>
                Stocks mat. 1ères & cons. <Tuto onClick={() => showTooltip('step_4_tuto_5.webp')} />
              </>
            }
            name='stocks_matiere_premiere'
            error={errors?.stocks_matiere_premiere && errors.stocks_matiere_premiere.message}
            icon='euro'
          />
          <NumberInput
            useForm={Form}
            onChange={calcTotalActifCirculant}
            label={
              <>
                Stocks marchandises <Tuto onClick={() => showTooltip('step_4_tuto_6.webp')} />
              </>
            }
            name='stocks_marchandise'
            error={errors?.stocks_marchandise && errors.stocks_marchandise.message}
            icon='euro'
          />
        </div>
        <div className='row-1000'>
          <NumberInput
            useForm={Form}
            onChange={calcTotalActifCirculant}
            label={
              <>
                Créances clients <Tuto onClick={() => showTooltip('step_4_tuto_7.webp')} />
              </>
            }
            name='creances_clients'
            error={errors?.creances_clients && errors.creances_clients.message}
            icon='euro'
          />
          <NumberInput
            useForm={Form}
            onChange={calcTotalActifCirculant}
            label={
              <>
                Autres créances <Tuto onClick={() => showTooltip('step_4_tuto_8.webp')} />
              </>
            }
            name='autres_creances'
            error={errors?.autres_creances && errors.autres_creances.message}
            icon='euro'
          />
        </div>
        <div className='row-1000'>
          <NumberInput
            useForm={Form}
            onChange={calcTotalActifCirculant}
            label={
              <>
                Valeurs mobilières de placement <Tuto onClick={() => showTooltip('step_4_tuto_9.webp')} />
              </>
            }
            name='valeurs_mobilieres_placement'
            error={errors?.valeurs_mobilieres_placement && errors.valeurs_mobilieres_placement.message}
            icon='euro'
          />
          <NumberInput
            useForm={Form}
            onChange={calcTotalActifCirculant}
            label={
              <>
                Disponibilités <Tuto onClick={() => showTooltip('step_4_tuto_10.webp')} />
              </>
            }
            name='disponibilites'
            error={errors?.disponibilites && errors.disponibilites.message}
            icon='euro'
          />
        </div>
        <NumberInput
          useForm={Form}
          onChange={calcTotalActifCirculant}
          label={
            <>
              Charges constatées d'avance <Tuto onClick={() => showTooltip('step_4_tuto_11.webp')} />
            </>
          }
          name='charges_constatees_avance'
          error={errors?.charges_constatees_avance && errors.charges_constatees_avance.message}
          icon='euro'
        />
        <NumberInput
          bgColor='var(--main-color)'
          disabled
          useForm={Form}
          label='total actif circulant'
          name='total_actif_circulant'
          error={errors?.total_actif_circulant && errors.total_actif_circulant.message}
          validation={{}}
          icon='euro'
        />
        <NumberInput
          useForm={Form}
          onChange={calcTotalActif}
          label={
            <>
              Comptes de régularisation <Tuto onClick={() => showTooltip('step_4_tuto_12.webp')} />
            </>
          }
          name='comptes_regularisation'
          error={errors?.comptes_regularisation && errors.comptes_regularisation.message}
          icon='euro'
        />
        <NumberInput
          bgColor='var(--main-color)'
          disabled
          useForm={Form}
          label='total actif'
          name='total_actif'
          error={errors?.total_actif && errors.total_actif.message}
          validation={{}}
          icon='euro'
        />
        {!secondaryParcours && (
          <Textarea
            register={register}
            error={errors?.note_bilan_actif && errors.note_bilan_actif.message}
            name='note_bilan_actif'
            label='notes et remarques'
            className='mt-md'
            onFocus={() => {
              if (step == index + 1) setStep(index);
            }}
            onKeyDown={e => {
              if (e.keyCode === 9 && !e.shiftKey && !e.ctrlKey) {
                e.preventDefault();
                handleSubmit(formSubmit)();
              }
            }}
          />
        )}
      </fieldset>
    </form>
  );
}

export default BilanActif;
